import React, { useEffect, useState } from 'react';
import { logger } from '../utils/logger';

interface VersionInfo {
    version: string;
    buildTime: string;
    buildNumber?: number;
}

const VersionFooter: React.FC = () => {
    const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const fetchVersionInfo = async () => {
            try {
                // Force fresh fetch zonder cache
                const response = await fetch('/version.json', {
                    cache: 'no-cache',
                    headers: {
                        'Cache-Control': 'no-cache',
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setVersionInfo(data);
                } else {
                    // Fallback voor development
                    setVersionInfo({
                        version: process.env.REACT_APP_VERSION || '0.0.0-dev',
                        buildTime: new Date().toISOString(),
                        buildNumber: Math.floor(Date.now() / 1000)
                    });
                }
            } catch (error) {
                logger.warn('Could not fetch version info:', error);
                // Fallback voor development
                setVersionInfo({
                    version: process.env.REACT_APP_VERSION || '0.0.0-dev',
                    buildTime: new Date().toISOString(),
                    buildNumber: Math.floor(Date.now() / 1000)
                });
            }
        };

        fetchVersionInfo();

        // Update elke 5 minuten
        const interval = setInterval(fetchVersionInfo, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    if (!versionInfo) {
        return null;
    }

    const formatBuildTime = (buildTime: string) => {
        try {
            const date = new Date(buildTime);
            return date.toLocaleString('nl-NL', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return buildTime;
        }
    };

    const VersionDisplay = ({ isMobile = false }: { isMobile?: boolean }) => (
        <div
            className={`text-xs transition-all duration-300 ${isMobile
                    ? 'p-2 bg-gray-50 text-gray-400 border-t border-gray-200'
                    : isExpanded
                        ? 'p-3 rounded-lg bg-white text-gray-600 border border-gray-200 shadow-lg'
                        : 'p-1.5 px-2 rounded-md bg-gray-50 text-gray-400 border border-gray-200 shadow-sm hover:bg-white hover:text-gray-500 hover:shadow-md opacity-60 hover:opacity-100'
                }`}
        >
            <div
                className={`cursor-pointer flex items-center ${isMobile ? 'justify-center space-x-1' : 'space-x-1'}`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span className="font-mono text-xs opacity-75">v{versionInfo.version}</span>
                {isExpanded && !isMobile && (
                    <svg
                        className="w-3 h-3 opacity-50"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                )}
            </div>

            {isExpanded && !isMobile && (
                <div className="mt-2 space-y-1 border-t border-gray-200 pt-2">
                    <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-xs">Build:</span>
                        <span className="font-mono text-xs text-gray-500">
                            {versionInfo.buildNumber || 'Unknown'}
                        </span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-xs">Tijd:</span>
                        <span className="font-mono text-xs text-gray-500">
                            {formatBuildTime(versionInfo.buildTime)}
                        </span>
                    </div>
                    <div className="pt-1">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                window.location.reload();
                            }}
                            className="text-gray-400 hover:text-gray-600 underline text-xs"
                        >
                            refresh
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <>
            {/* Desktop versie - fixed rechtsonder */}
            <div className="hidden md:block fixed bottom-2 right-2 z-50">
                <VersionDisplay />
            </div>

            {/* Mobile versie - normale flow onderaan pagina */}
            <div className="block md:hidden w-full">
                <VersionDisplay isMobile />
            </div>
        </>
    );
};

export default VersionFooter; 